// Pixel scene renderer for the 400x225 stage. Only touches the canvas it is given.
import { drawVisitor } from './visitors.js';
export const W = 400, H = 225;

const P = { wall: '#1d2b33', wall2: '#2f4a4a', woodD: '#2e1c12', wood: '#5e3a22', woodL: '#8a5a32', sod: '#f0a640', glow: '#ffd98a',
  paper: '#e9dcb8', paperS: '#c9b98e', pink: '#d0607a', skin: '#c99a7a', skinS: '#8f6352', coatL: '#3e3650', metal: '#3b4447',
  metalL: '#5d6a6b', black: '#05060b' };
const BAYER = [[0, 8, 2, 10], [12, 4, 14, 6], [3, 11, 1, 9], [15, 7, 13, 5]];

function painter(g) {
  const R = (x, y, w, h, c) => { g.fillStyle = c; g.fillRect(x, y, w, h); };
  const dith = (x, y, w, h, a, b, f) => {
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) R(x + i, y + j, 1, 1, f(i / w, j / h) * 16 > BAYER[(y + j) & 3][(x + i) & 3] ? b : a);
  };
  const disc = (cx, cy, r, fn) => {
    for (let y = -r; y <= r; y++) for (let x = -r; x <= r; x++) { const d = Math.hypot(x, y); if (d <= r) fn(cx + x, cy + y, d / r); }
  };
  return { g, R, dith, disc };
}

function drawRoom({ R, dith, disc }) {
  dith(0, 0, W, H, P.wall, P.wall2, u => 0.25 + 0.125 * Math.sin(u * 80) + (1 - Math.abs(u - 0.48) * 1.6) * 0.35);
  R(0, 196, W, 29, P.woodD); R(0, 192, W, 4, '#162027');
  R(118, 14, 164, 182, P.woodD);
  dith(124, 20, 152, 176, P.wood, P.woodL, (u, v) => 0.3 + 0.25 * Math.sin(u * 40) + 0.15 * (1 - v));
  R(134, 30, 132, 60, '#4a2e1b'); R(134, 104, 132, 80, '#4a2e1b');
  dith(136, 32, 128, 56, P.wood, '#4a2e1b', () => 0.35); dith(136, 106, 128, 76, P.wood, '#4a2e1b', () => 0.35);
  R(252, 98, 10, 3, P.metalL); for (let i = 0; i < 5; i++) R(240 + i * 3, 102 + (i % 2), 2, 2, P.metalL);
  disc(262, 118, 3, (x, y, d) => R(x, y, 1, 1, d < 0.5 ? P.glow : P.sod));
  R(170, 2, 60, 13, P.black); R(171, 3, 58, 11, '#11160f');
  R(10, 20, 80, 52, P.paperS); R(11, 21, 78, 50, P.paper); R(40, 17, 20, 6, '#d9d2bdaa');
  R(10, 80, 80, 40, P.paper); R(46, 77, 8, 6, P.pink);
  R(14, 134, 72, 14, P.metal); R(16, 136, 68, 10, P.metalL); R(20, 140, 60, 2, P.black);
  R(20, 146, 60, 30, P.paper); for (let i = 0; i < 60; i += 4) R(20 + i, 176, 2, 2, P.paper);
  R(102, 166, 32, 28, '#7fa79b55'); R(100, 164, 36, 4, P.metalL);
  R(278, 20, 114, 166, P.metal); R(280, 22, 110, 162, P.metalL); R(281, 23, 108, 1, '#7d8b8c');
  for (let y = 0; y < 6; y++) for (let x = 0; x < 18; x++) R(286 + x * 6, 28 + y * 4, 3, 2, P.metal);
  R(282, 60, 106, 96, P.black); R(282, 157, 106, 13, P.black);
  R(146, 200, 136, 22, P.woodD);
}

// Gold and red knocks recolour the inside of the door: wood shades map onto a metal ramp.
const SPECIAL = { gold: ['#4a3410', '#8a6420', '#d9a42e', '#ffe08a'], red: ['#3a0e0c', '#6e1a16', '#b0302a', '#ff7a5c'] };
function tintDoor(room, ramp) {
  const c = document.createElement('canvas'); c.width = 152; c.height = 176;
  const g = c.getContext('2d');
  g.drawImage(room, 124, 20, 152, 176, 0, 0, 152, 176);
  const img = g.getImageData(0, 0, 152, 176), d = img.data;
  const rgb = ramp.map(h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16)));
  for (let i = 0; i < d.length; i += 4) {
    const px = (i / 4) % 152, py = Math.floor(i / 4 / 152);
    if (px < 12 && py > 142) continue; // leave the prize jar alone
    const lum = (d[i] * 0.3 + d[i + 1] * 0.59 + d[i + 2] * 0.11) / 255;
    const k = Math.min(3, Math.floor(lum * 6.5));
    [d[i], d[i + 1], d[i + 2]] = rgb[k];
  }
  g.putImageData(img, 0, 0);
  return c;
}

export function createScene(ctx) {
  const room = document.createElement('canvas');
  room.width = W; room.height = H;
  drawRoom(painter(room.getContext('2d')));
  const tinted = {};
  const doorFor = kind => tinted[kind] ?? (tinted[kind] = tintDoor(room, SPECIAL[kind]));
  const { R } = painter(ctx);
  const rain = Array.from({ length: 40 }, (_, i) => ({ x: (i * 37) % 140, y: (i * 53) % 140, s: 2 + (i % 3) }));
  let t = 0;

  function lens(cx, cy, r, flick, inner, sick) {
    const glow = sick ? '#c8dc8a' : P.glow, sod = sick ? '#8fae4a' : P.sod, mid = sick ? '#3e4a2a' : '#5a4a35';
    for (let y = -r - 5; y <= r + 5; y++) for (let x = -r - 5; x <= r + 5; x++) {
      const dist = Math.hypot(x, y);
      if (dist > r + 5) continue;
      const px = cx + x, py = cy + y, d = dist / r;
      if (d > 1) { R(px, py, 1, 1, dist > r + 4 ? P.metal : P.metalL); continue; }
      const u = x / r, v = y / r, b = BAYER[py & 3][px & 3];
      let c;
      if (v > 0.35) c = (Math.floor(px / 6) + Math.floor(py / 4)) % 2 ? '#2b2a24' : '#33302a';
      else {
        const lamp = Math.max(0, 1 - Math.hypot(u, v + 0.75) * 1.1) * flick;
        c = lamp * 16 > b + 2 ? (lamp > 0.55 ? glow : sod) : lamp * 24 > b ? mid : '#2a2a2c';
      }
      if (d > 0.72 && (d - 0.72) * 60 > b) c = P.black;
      R(px, py, 1, 1, c);
    }
    R(cx - 4, cy - 58, 8, 3, glow);
    ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r * 0.8, 0, 7); ctx.clip();
    for (const d of rain) {
      d.y += d.s; d.x -= 0.6;
      if (d.y > 140) { d.y = -5; d.x = (d.x + 97) % 150; }
      R(cx - 70 + Math.round(d.x), cy - 70 + Math.round(d.y), 1, sick ? 1 : 3, sick ? '#7f9a5a' : '#55617a');
    }
    inner();
    ctx.restore();
  }

  // Outbreak: hazard tape across the door frame and a hazard sign.
  function quarantine() {
    for (let i = 0; i < 70; i++) {
      const c = Math.floor(i / 5) % 2 ? '#141010' : '#f0c040';
      R(118 + i, 150 + Math.round(i * 0.45), 2, 4, c);
      R(281 - i, 150 + Math.round(i * 0.45), 2, 4, c);
    }
    R(186, 186, 28, 8, '#f0c040'); R(187, 187, 26, 6, '#141010'); R(196, 188, 8, 2, '#f0c040'); R(199, 190, 2, 2, '#f0c040');
  }

  return {
    // state: { role, flicker, visitor: look|null (see visitors.js), reveal: 'human'|'alien'|'infected'|null, outbreak, timerFrac, shelter:{left,total}, coins }
    draw(state) {
      t++;
      ctx.drawImage(room, 0, 0);
      if (SPECIAL[state.special] && state.role !== 'visitor') {
        ctx.drawImage(doorFor(state.special), 124, 20);
        const [, , hot, glint] = SPECIAL[state.special], pulse = 0.5 + 0.5 * Math.sin(t / 9);
        for (let i = 0; i < 164; i += 2) { if ((i + t) % 7 < 4 * pulse) { R(118 + i, 14, 2, 2, hot); R(118 + i, 194, 2, 2, hot); } }
        for (let j = 0; j < 182; j += 2) { if ((j + t) % 7 < 4 * pulse) { R(118, 14 + j, 2, 2, hot); R(280, 14 + j, 2, 2, hot); } }
        if (state.special === 'gold') for (let k = 0; k < 6; k++) { const n = (k * 97 + Math.floor(t / 6) * 31) % 997; if (n % 5 === 0) R(130 + (n * 7) % 140, 26 + (n * 13) % 160, 1, 1, glint); }
      }
      const cx = 200, cy = 96, r = 62;
      if (state.role === 'visitor') {
        lens(cx, cy, r, state.flicker, () => {
          R(cx - 26, cy - 40, 52, 90, P.wood); R(cx - 26, cy - 40, 52, 3, P.woodD);
          R(cx - 2, cy - 14, 4, 4, P.glow); R(cx + 16, cy + 8, 3, 3, P.sod);
        }, state.outbreak);
      } else {
        lens(cx, cy, r, state.flicker, () => { if (state.visitor) drawVisitor(R, cx, cy, state.visitor, t, { reveal: state.reveal }); }, state.outbreak);
      }
      const tl = Math.max(0, Math.min(1, state.timerFrac ?? 0));
      for (let a = 0; a < 64; a++) {
        const ang = -Math.PI / 2 + (a / 64) * Math.PI * 2;
        R(Math.round(cx + Math.cos(ang) * (r + 9)), Math.round(cy + Math.sin(ang) * (r + 9)), 2, 2,
          a / 64 < tl ? (tl < 1 / 6 ? P.pink : P.sod) : '#1a2226');
      }
      const left = state.shelter?.left ?? 10, total = state.shelter?.total ?? 10;
      for (let i = 0; i < total; i++) {
        const c = i < left ? P.sod : '#2a3438', x = 34 + i * 8;
        R(x, 7, 6, 4, c); R(x + 1, 5, 4, 2, c); R(x + 2, 4, 2, 1, c);
      }
      for (let i = 0; i < Math.min(9, state.coins ?? 0); i++) R(106 + (i * 7) % 24, 180 + ((i * 5) % 12), 3, 2, P.glow);
      if (state.outbreak) quarantine();
      R(384, 26, 3, 3, t % 60 < 30 ? P.pink : '#5a2633');
    },
  };
}
